<template lang="pug">
  v-bottom-sheet(v-if="showDialog" v-model="showDialog" persistent)
    v-card.round-top-xl
      v-card-title.px-4
        v-spacer
        .text-md.font-semibold.ml-n4 Edit info
        v-spacer
        v-btn(icon color="neutral700" @click="closeDialog")
          v-icon mdi-close
      v-card-text
        div.text-sm.font-semibold.neutral700--text.mt-5 Phone number
        phone-input.mt-2(v-model="phone" ref="phoneInput" required)
        template(v-if="isNameEnabled")
          div.text-sm.font-semibold.neutral700--text.mt-5 Name&nbsp;
            span(v-if="!isNameRequired") (optional)
          v-text-field.text-sm.mt-2.round-xl(v-model="name" placeholder="Enter name here" outlined dense height="48" :rules="isNameRequired ? rules.not_empty : []" hide-details="auto")
        template(v-if="isEmailEnabled")
          div.text-sm.font-semibold.neutral700--text.mt-5 Email&nbsp;
            span(v-if="!isEmailRequired") (optional)
          v-text-field.text-sm.mt-2.round-xl(v-model="email" placeholder="Enter email here" outlined dense height="48" hide-details="auto" :rules="isEmailRequired ? rules.email : email && email.length > 0 ? rules.email : []" type="email")
        template(v-if="loyaltySettings.marketing.plan === 'standalone'")
          div.text-sm.font-semibold.neutral700--text.mt-4 {{ $t('date_of_birth') }}
          v-text-field.text-sm.mt-2.round-xl(v-model="dob" placeholder="Enter date of birth" type="date" outlined dense height="48" hide-details="auto" :rules="rules.not_empty")
        v-btn.button-md.mt-10.mb-4.white--text(block depressed color="neutral700" :loading="isLoading" :disabled="!canProceed" @click="handleSave") Save
</template>

<script>
import PhoneInput from "@/components/clinic/custom/PhoneInput.vue";
import {mapActions, mapGetters} from "vuex";

export default {
  components: {
    PhoneInput
  },
  props: {
    open: {
      type: Boolean,
    },
    parentId: {
      type: Number,
      required: true
    },
    parentName: {
      type: String,
      default: null
    },
    parentPhone: {
      type: String,
      default: null
    },
    parentEmail: {
      type: String,
      default: null
    },
    parentDob: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      phone: null,
      name: null,
      email: null,
      dob: null,
      isLoading: false,
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        email: [v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid',]
      },
    }
  },
  mounted() {
    this.phone = this.parentPhone
    this.name = this.parentName
    this.email = this.parentEmail
    this.dob = this.parentDob
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      loyaltySettings: 'marketing/getLoyaltySettings'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    isNameEnabled() {
      return Boolean(this.loyaltySettings?.page_properties?.find(prop => prop.name === 'Name')?.enabled)
    },
    isEmailEnabled() {
      return Boolean(this.loyaltySettings?.page_properties?.find(prop => prop.name === 'Email')?.enabled)
    },
    isNameRequired() {
      return Boolean(this.loyaltySettings?.page_properties.find(prop => prop.name === 'Name')?.required)
    },
    isEmailRequired() {
      return Boolean(this.loyaltySettings?.page_properties.find(prop => prop.name === 'Email')?.required)
    },
    canProceed() {
      let baseValidation = Boolean(this.phone && !this.isPhoneError)
      if (this.isNameRequired) {
        baseValidation = baseValidation && Boolean(this.name)
      }
      if (this.isEmailRequired) {
        baseValidation = baseValidation && Boolean(this.email)
      }
      if (this.loyaltySettings.marketing.plan === 'standalone') {
        baseValidation = baseValidation && Boolean(this.dob)
      }
      return baseValidation
    },
    isPhoneError() {
      return this.$refs.phoneInput.phoneAlertMessage.length > 0
    },
    payload() {
      const payload = {
        phone: this.phone,
        name: this.name,
        email: this.email
      }
      if (this.loyaltySettings.marketing.plan === 'standalone') {
        payload.dob = this.dob
      }
      return payload
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    closeDialog() {
      this.showDialog = false
      this.$emit('close')
    },
    async handleSave() {
      const url = `/loyalty/${this.company.slug}/members/${this.parentId}`
      try {
        this.isLoading = true
        const {data} = await this.$axios.patch(url, this.payload)
        this.updateActivityLog(url, this.payload, data, 'PATCH', 'Edit info', this.parentId)
        this.$store.commit('marketing/setMember', data)
        this.showMessage(['success', 'Successfully updated'])
        this.closeDialog()
      } catch (error) {
        if (error.response.status === 400) {
          if (error.response.data?.detail.includes('phone number has been used')) {
            this.showMessage(['error', 'Phone number has been used'])
          } else {
            this.showMessage(['error', error.response.data])
          }
        } else {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        }
      } finally {
        this.isLoading = false
      }
    },
    updateActivityLog(endpoint, payload, response, method, activity, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'Edit info',
        activity: `Merchant-app: ${activity}`,
        raw_data: {endpoint, payload, response, method}
      }
      this.$axios.post('/users/logs', data)
          .catch(() => {
          })
    },
  },
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}

.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>